/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';
import { get as userGet } from 'js/lib/user';

import { Grid } from '@coursera/cds-core';

import ConsumerSearchBarGridItem from 'bundles/composable-page-header-consumer/components/ConsumerSearchBarGridItem';
import ExploreMegaMenuGridItem from 'bundles/composable-page-header-consumer/components/ExploreMegaMenuGridItem';
import ShoppingCartGridItem from 'bundles/composable-page-header-consumer/components/ShoppingCartGridItem';
import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import { useShoppingCart } from 'bundles/composable-page-header-consumer/contexts/ShoppingCartContext';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import LanguageDropdownLegacyGridItem from 'bundles/composable-page-header/components/grid-items/LanguageDropdownLegacyGridItem';
import MyLearningGridItem from 'bundles/composable-page-header/components/grid-items/MyLearningGridItem';
import NavLinkGridItem from 'bundles/composable-page-header/components/grid-items/NavLinkGridItem';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import ProgramSwitcherGridItem from 'bundles/composable-page-header/components/grid-items/ProgramSwitcherGridItem';
import CoachStudioGridItem, {
  showCoachStudio,
} from 'bundles/composable-page-header/components/grid-items/coach/CoachStudioGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import DefaultAccountDropdownGridItemRenderer from 'bundles/composable-page-header/module-renderers/DefaultAccountDropdownGridItemRenderer';
import isOnDegreePage from 'bundles/composable-page-header/utils/isOnDegreePage';
import ProfitwellTag from 'bundles/payments-common/utils/ProfitwellTag';
import { LOGGED_IN_HOME_PAGE_TYPE_TO_SLUG } from 'bundles/unified-home-common/constants';

import _t from 'i18n!nls/composable-page-header-consumer';

const styles = {
  leftSide: css`
    width: auto;
  `,
  middleSide: css`
    flex-shrink: 1;
    flex-grow: 1;
    flex-basis: 0%;
    padding: 10px 16px;
    width: 100%;
  `,
  rightSide: css`
    width: auto;
  `,
  exploreItem: css`
    display: flex;
    flex-direction: row;
  `,
};

type Props = {
  showDiscoveryElements?: boolean;
};

const LoggedInConsumerNoSubNavDesktopMainNav = ({ showDiscoveryElements = true }: Props) => {
  const {
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    chosenDegree,
    chosenEnterpriseProgram,
    hasEnrolledPrograms,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    isCourseraPlusLearner,
    courseraPlusSubscriptionCycle,
  } = useConsumerMyPageHeader();
  const shoppingCart = useShoppingCart();
  const location = useLocation();
  const userEmail = userGet()?.email_address;
  const { MY_LEARNING } = LOGGED_IN_HOME_PAGE_TYPE_TO_SLUG;

  return (
    <>
      {userEmail && <ProfitwellTag userEmail={userEmail} />}
      <MainNavigationWrapper>
        <Grid item container alignItems="center" spacing={8} css={styles.leftSide}>
          {hasEnrolledPrograms && (
            <Grid item>
              <ProgramSwitcherGridItem
                currentProgram={chosenEnterpriseProgram}
                currentDegree={chosenDegree}
                programs={enrolledEnterprisePrograms}
                degrees={enrolledDegreePrograms}
              />
            </Grid>
          )}
          <Grid item>
            <HomeLogoGridItemModule href="/" logos={[<CourseraHomeLogo key="1" />]} />
          </Grid>
          {showDiscoveryElements && (
            <Grid item>
              <ExploreMegaMenuGridItem />
            </Grid>
          )}
          <Grid item>
            <MyLearningGridItem
              label={_t('My Learning')}
              href={`/${MY_LEARNING}`}
              name="my_learning"
              trackingName="lihp_my_learning"
            />
          </Grid>
          <NavLinkGridItem
            label={_t('Degrees')}
            href="/degrees"
            name="degrees"
            trackingName="logged_in_online_degrees"
            isCurrent={isOnDegreePage(location.pathname)}
            isButton
          />
        </Grid>
        <Grid item container alignItems="center" css={styles.middleSide}>
          {showDiscoveryElements && <ConsumerSearchBarGridItem />}
        </Grid>
        <Grid item container alignItems="center" justifyContent="flex-end" css={styles.rightSide}>
          {shoppingCart?.isValidCart && (
            <Grid item>
              <ShoppingCartGridItem />
            </Grid>
          )}
          <LanguageDropdownLegacyGridItem
            currentThirdPartyOrgId={chosenEnterpriseProgram?.naptimeThirdPartyOrganization?.id}
          />
          <Grid item>
            <NotificationsLegacyGridItem />
          </Grid>
          {showCoachStudio() && (
            <Grid item>
              <CoachStudioGridItem />
            </Grid>
          )}
          <Grid item>
            <DefaultAccountDropdownGridItemRenderer
              hasEnrolledPrograms={!!hasEnrolledPrograms}
              naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
              hasCheatingIncidents={!!hasCheatingIncidents}
              canViewEducatorAdminLink={!!canViewEducatorAdminLink}
              isCourseraPlusLearner={!!isCourseraPlusLearner}
              courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
            />
          </Grid>
        </Grid>
      </MainNavigationWrapper>
    </>
  );
};

export default LoggedInConsumerNoSubNavDesktopMainNav;
